<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import FollowUpCadenceAPI from 'dashboard/api/followUpCadence';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});
const { t } = useI18n();
const cadence = ref(null);

// Cadência pode não existir para a conta (feature desligada) ou a busca pode
// falhar (ex.: conta sem migração) — em qualquer um dos casos não mostramos
// nada, para não quebrar o restante do painel de funil.
const load = async () => {
  try {
    const { data } = await FollowUpCadenceAPI.get(props.conversationId);
    cadence.value = data.cadence;
  } catch {
    cadence.value = null;
  }
};

// Falha ao parar (rede, 500) não pode derrubar o painel: o status fica como
// estava e o botão continua lá para tentar de novo.
const stop = async () => {
  try {
    const { data } = await FollowUpCadenceAPI.stop(props.conversationId);
    cadence.value = data.cadence;
  } catch {
    // mantém o status atual
  }
};

const nextTouch = computed(() =>
  cadence.value?.next_touch_at
    ? new Date(cadence.value.next_touch_at).toLocaleString('pt-BR', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : ''
);
const live = computed(() =>
  ['active', 'waiting_reply', 'snoozed'].includes(cadence.value?.status)
);
const stepLabel = computed(() =>
  t('FUNNEL_TIMELINE.STATUS_STEP', {
    step: (cadence.value?.step_index || 0) + 1,
    total: cadence.value?.total_steps || 0,
  })
);

onMounted(load);
</script>

<template>
  <div
    v-if="cadence"
    class="mt-1 flex items-center justify-between gap-2 text-xs text-n-slate-11"
  >
    <span v-if="cadence.status === 'active'">
      {{ stepLabel }} ·
      {{ t('FUNNEL_TIMELINE.STATUS_NEXT', { when: nextTouch }) }}
      <span class="text-n-slate-10">
        ({{
          t('FUNNEL_TIMELINE.STATUS_BEST_HOUR', { hour: cadence.best_hour })
        }})
      </span>
    </span>
    <span v-else-if="cadence.status === 'waiting_reply'">
      {{ stepLabel }} · {{ t('FUNNEL_TIMELINE.STATUS_WAITING_REPLY') }}
    </span>
    <span v-else-if="cadence.status === 'finished'">{{
      t('FUNNEL_TIMELINE.STATUS_FINISHED')
    }}</span>
    <span v-else-if="cadence.status === 'stopped'">
      {{
        t(
          `FUNNEL_TIMELINE.STOP_REASON_${(cadence.stop_reason || 'manual').toUpperCase()}`
        )
      }}
    </span>
    <button
      v-if="live"
      type="button"
      data-test="stop-cadence"
      class="text-n-ruby-11 hover:underline"
      @click="stop"
    >
      {{ t('FUNNEL_TIMELINE.STATUS_STOP') }}
    </button>
  </div>
</template>
